import React from 'react';
import { useTheme } from '../../../theme/ThemeProvider';

const MetricElement = ({ 
  element, 
  processedData,  // Datos procesados del backend
  isEditing = false 
}) => {
  const { mode } = useTheme();
  const config = element.config || {};
  
  // Extraer datos procesados del backend
  const metricValue = processedData?.value;
  const formatted = processedData?.formatted || (metricValue !== null && metricValue !== undefined ? metricValue.toString() : 'N/A');
  const comparisonValue = processedData?.comparison_value;
  const delta = processedData?.delta;
  const deltaPercentage = processedData?.delta_percentage;
  const error = processedData?.error;
  
  const getColorByThreshold = () => {
    const { thresholds } = config;
    
    if (!thresholds || metricValue === null) {
      return mode === 'dark' ? '#29B5E8' : '#1B96C7';
    }
    
    const { good, warning, bad } = thresholds;
    
    if (good !== undefined && metricValue >= good) {
      return '#38C172';
    }
    if (warning !== undefined && metricValue >= warning) {
      return '#F59E0B';
    }
    if (bad !== undefined) {
      return '#EF4444';
    }
    
    return mode === 'dark' ? '#29B5E8' : '#1B96C7';
  };
  
  const styles = {
    container: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--spacing-lg)',
      textAlign: 'center'
    },
    icon: {
      fontSize: '32px',
      marginBottom: 'var(--spacing-sm)',
      opacity: 0.8
    },
    value: {
      fontSize: 'clamp(2rem, 5vw, 4rem)',
      fontWeight: 'var(--font-weight-bold)',
      color: getColorByThreshold(),
      marginBottom: 'var(--spacing-xs)',
      lineHeight: 1.2
    },
    label: {
      fontSize: 'var(--font-size-sm)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)',
      marginBottom: 'var(--spacing-md)',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontWeight: 'var(--font-weight-semibold)'
    },
    deltaContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--spacing-sm)',
      fontSize: 'var(--font-size-base)',
      fontWeight: 'var(--font-weight-semibold)',
      marginTop: 'var(--spacing-sm)'
    },
    deltaPositive: {
      color: '#38C172'
    },
    deltaNegative: {
      color: '#EF4444'
    },
    deltaNeutral: {
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)'
    },
    arrow: {
      fontSize: '16px'
    },
    placeholder: {
      textAlign: 'center',
      color: mode === 'dark' ? 'var(--dark-text-tertiary)' : 'var(--color-medium-gray)',
      padding: 'var(--spacing-xl)'
    },
    placeholderIcon: {
      fontSize: '48px',
      marginBottom: 'var(--spacing-md)',
      opacity: 0.5
    },
    error: {
      color: '#FF6B35',
      padding: 'var(--spacing-md)',
      textAlign: 'center'
    }
  };
  
  // Renderizado condicional
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          ⚠️ {error}
        </div>
      </div>
    );
  }
  
  if (!processedData || metricValue === null || metricValue === undefined) {
    return (
      <div style={styles.container}>
        <div style={styles.placeholder}>
          <div style={styles.placeholderIcon}>📈</div>
          <div>{isEditing ? 'Configura la métrica' : 'Ejecutando...'}</div>
        </div>
      </div>
    );
  }
  
  return (
    <div style={styles.container}>
      {/* Icon */}
      {config.icon && (
        <div style={styles.icon}>
          {config.icon}
        </div>
      )}
      
      {/* Value */}
      <div style={styles.value}>
        {formatted}
      </div>
      
      {/* Label */}
      {config.label && (
        <div style={styles.label}>
          {config.label}
        </div>
      )}
      
      {/* Delta (comparison) */}
      {delta !== null && delta !== undefined && deltaPercentage !== null && deltaPercentage !== undefined && (
        <div style={styles.deltaContainer}>
          <span 
            style={{
              ...styles.arrow,
              ...(deltaPercentage > 0 
                ? styles.deltaPositive 
                : deltaPercentage < 0 
                ? styles.deltaNegative 
                : styles.deltaNeutral)
            }}
          >
            {deltaPercentage > 0 ? '▲' : deltaPercentage < 0 ? '▼' : '━'}
          </span>
          <span 
            style={
              deltaPercentage > 0 
                ? styles.deltaPositive 
                : deltaPercentage < 0 
                ? styles.deltaNegative 
                : styles.deltaNeutral
            }
          >
            {Math.abs(deltaPercentage).toFixed(1)}%
          </span>
          <span style={{ fontSize: 'var(--font-size-sm)', opacity: 0.7 }}>
            ({delta > 0 ? '+' : ''}{delta.toFixed(0)})
          </span>
        </div>
      )}
    </div>
  );
};

export default MetricElement;
